import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, signal, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth.service';
import { ZoomControlComponent } from 'src/app/shared/components/ui/zoom-control/zoom-control.component';
import { WhiteboardPartComponent } from 'src/app/shared/components/whiteboard/whiteboard-part.component';
import { ZoomableDirective } from 'src/app/shared/directives/zoomable.directive';
import { ContentStateService } from '../../../content-state.service';
import { Chunk } from '../../chunk.dto';
import { ChunkService } from '../../chunk.service';
import { WhiteboardDialogComponent } from './whiteboard-dialog.component';

@Component({
  selector: 'app-whiteboard-chunk-displayer',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, TranslateModule, WhiteboardPartComponent, ZoomControlComponent, ZoomableDirective],
  styleUrls: ['./whiteboard-chunk-displayer.scss'],
  template: `
    @if (chunk(); as chunk) {
      <div class="whiteboard-card">
        <div class="whiteboard-card-header">
          <div class="whiteboard-icon-box">
            <mat-icon class="whiteboard-icon">gesture</mat-icon>
          </div>
          @if (isEditingTitle()) {
            <div class="edit-title-container" (click)="$event.stopPropagation()">
              <input #titleInput type="text" [value]="tempTitle()" (keydown.enter)="saveTitle(titleInput.value)" (keydown.escape)="cancelEdit()" class="title-edit-input" />
              <button mat-icon-button class="edit-action-btn confirm-btn" (click)="saveTitle(titleInput.value)">
                <mat-icon>check</mat-icon>
              </button>
              <button mat-icon-button class="edit-action-btn cancel-btn" (click)="cancelEdit()">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          } @else {
            <span class="whiteboard-card-title" [class.editable]="isWriter()" (click)="isWriter() && startEdit($event)">
              {{ formattedTitle() }}
            </span>
          }
          <div class="whiteboard-card-actions">
            <button mat-icon-button class="download-btn" (click)="$event.stopPropagation(); downloadBoard()" [matTooltip]="'Global.Download' | translate">
              <mat-icon class="download-icon">download</mat-icon>
            </button>
            <button mat-icon-button class="open-btn" (click)="$event.stopPropagation(); openEditor()" [matTooltip]="(isWriter() ? 'Global.Edit' : 'Global.Open') | translate">
              <mat-icon class="open-icon">expand_content</mat-icon>
            </button>
          </div>
        </div>
        <div
          #zoom="appZoomable"
          appZoomable
          [disableTouchMove]="true"
          [zoomScale]="0.8"
          [enableWheelZoom]="false"
          [minScale]="0.5"
          [maxScale]="3.0"
          class="whiteboard-preview-area"
          [class.grabbing]="zoom.isDragging()"
        >
          <app-zoom-control
            class="zoom-toolbar"
            [(zoomScale)]="zoom.zoomScale"
            [minScale]="0.5"
            [maxScale]="3.0"
            [showFullScreen]="true"
            (fullscreen)="openFullScreen()"
            (reset)="zoom.resetZoom()"
            (click)="$event.stopPropagation()"
          />

          <div style="width: 100%; height: 100%;" class="pointer-events-none">
            <app-whiteboard-part
              [color]="'#333333'"
              [size]="5"
              [boardID]="boardID"
              [zoomScale]="zoom.zoomScale()"
              [panX]="zoom.panX()"
              [panY]="zoom.panY()"
              [drawHistory]="parsedData().drawHistory"
              [postits]="parsedData().postits"
              [renderPostItsOnCanvas]="true"
              style="pointer-events: none;"
            />
          </div>
        </div>
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WhiteboardChunkDisplayerComponent {
  chunk = input.required<Chunk>();
  boardID = Math.random();

  @ViewChild(WhiteboardPartComponent) whiteboardPartComponent!: WhiteboardPartComponent;

  private contentStateService = inject(ContentStateService);
  private authService = inject(AuthService);
  private chunkService = inject(ChunkService);
  private translate = inject(TranslateService);
  private dialog = inject(MatDialog);

  isEditingTitle = signal(false);
  tempTitle = signal('');

  @ViewChild('titleInput') set titleInput(element: ElementRef<HTMLInputElement>) {
    if (element) {
      const input = element.nativeElement;
      input.focus();
      const len = input.value.length;
      input.setSelectionRange(len, len);
    }
  }

  isWriter = computed(() => this.authService.isWriter() && !this.contentStateService.isReader());

  formattedTitle = computed(() => {
    const chunkData = this.chunk();
    if (!chunkData) return '';
    return chunkData.title || this.translate.instant('CreateChunk.Whiteboard');
  });

  parsedData = computed(() => {
    const edition = this.chunk()?.mainEdition || this.chunk()?.editions?.[0];
    const content = edition?.content || '{}';
    try {
      const data = typeof content === 'string' ? JSON.parse(content) : content;
      return {
        drawHistory: data.drawHistory || [],
        postits: data.postits || [],
      };
    } catch {
      return { drawHistory: [], postits: [] };
    }
  });

  startEdit(event: MouseEvent): void {
    event.stopPropagation();
    this.tempTitle.set(this.chunk().title || this.translate.instant('CreateChunk.Whiteboard'));
    this.isEditingTitle.set(true);
  }

  cancelEdit(): void {
    this.isEditingTitle.set(false);
  }

  saveTitle(newTitle: string): void {
    const trimmedTitle = newTitle.trim();
    if (!trimmedTitle) {
      this.isEditingTitle.set(false);
      return;
    }

    const chunkData = this.chunk();
    if (trimmedTitle === (chunkData.title || this.translate.instant('CreateChunk.Whiteboard'))) {
      this.isEditingTitle.set(false);
      return;
    }

    const edition = chunkData.mainEdition || chunkData.editions?.[0];
    if (!edition) {
      this.isEditingTitle.set(false);
      return;
    }

    this.chunkService
      .editChunk({
        id: chunkData._id,
        edition: edition,
        content: edition.content || '',
        contentID: chunkData.contentID || this.contentStateService.contentId,
        language: this.translate.currentLang,
        title: trimmedTitle,
      })
      .subscribe({
        next: () => {
          this.isEditingTitle.set(false);
        },
        error: (err) => {
          console.error('Failed to update chunk title', err);
          this.isEditingTitle.set(false);
        },
      });
  }

  downloadBoard(): void {
    if (this.whiteboardPartComponent) {
      this.whiteboardPartComponent.download();
    }
  }

  openEditor(): void {
    const edition = this.chunk()?.mainEdition || this.chunk()?.editions?.[0];
    this.contentStateService.openWhiteboardEditor({
      contentId: this.contentStateService.contentId,
      chunkId: this.chunk()._id,
      title: this.chunk().title || 'Whiteboard',
      edition: edition,
    });
  }

  openFullScreen() {
    this.dialog.open(WhiteboardDialogComponent, {
      data: {
        drawHistory: this.parsedData().drawHistory,
        postits: this.parsedData().postits,
        title: this.chunk().title || 'Whiteboard',
      },
      panelClass: 'mermaid-dialog-panel',
      width: '95vw',
      height: '95vh',
      maxWidth: '95vw',
      maxHeight: '95vh',
    });
  }
}
